'use client';

import jsPDF from 'jspdf';

export interface JobCardPDFData {
  jobNumber: string;
  date: string;
  technician: string;
  branch: string;
  vehicleReg: string;
  vin: string;
  make: string;
  model: string;
  year: string;
  mileage: string;
  faultReported: string;
  faultsFound: string;
  repairsDone: string;
  parts: { partNumber: string; partName: string; qty: number; stockAvailable: number }[];
  branchRepName: string;
  signatureData: string;
}

const COLORS = {
  darkBg: [15, 23, 42] as [number, number, number],
  primary: [37, 99, 235] as [number, number, number],
  danger: [239, 68, 68] as [number, number, number],
  warning: [245, 158, 11] as [number, number, number],
  positive: [34, 197, 94] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  lightGray: [241, 245, 249] as [number, number, number],
  midGray: [148, 163, 184] as [number, number, number],
  darkGray: [30, 41, 59] as [number, number, number],
  borderGray: [51, 65, 85] as [number, number, number],
  sectionBg: [22, 33, 55] as [number, number, number],
};

function setFont(doc: jsPDF, size: number, style: 'normal' | 'bold' = 'normal', color: [number, number, number] = COLORS.white) {
  doc.setFontSize(size);
  doc.setFont('helvetica', style);
  doc.setTextColor(...color);
}

function drawRect(doc: jsPDF, x: number, y: number, w: number, h: number, color: [number, number, number], radius = 0) {
  doc.setFillColor(...color);
  if (radius > 0) {
    doc.roundedRect(x, y, w, h, radius, radius, 'F');
  } else {
    doc.rect(x, y, w, h, 'F');
  }
}

function drawBorderedRect(doc: jsPDF, x: number, y: number, w: number, h: number, fillColor: [number, number, number], borderColor: [number, number, number], radius = 2) {
  doc.setFillColor(...fillColor);
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, radius, radius, 'FD');
}

function wrapText(doc: jsPDF, text: string, x: number, y: number, maxWidth: number, lineHeight: number): number {
  const lines = doc.splitTextToSize(text || '—', maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

export async function generateJobCardPDF(data: JobCardPDFData): Promise<string> {
  const doc = buildPDFDoc(data);
  const fileName = `JobCard_${data.jobNumber.replace('#', '')}_${data.vehicleReg.replace(/\s/g, '_')}.pdf`;
  doc.save(fileName);
  // Return base64 (without the data URI prefix) for Drive upload
  return doc.output('datauristring').split(',')[1];
}

export async function generateJobCardPDFBase64(data: JobCardPDFData): Promise<string> {
  const doc = buildPDFDoc(data);
  return doc.output('datauristring').split(',')[1];
}

function buildPDFDoc(data: JobCardPDFData): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;
  const pageH = 297;
  const margin = 14;
  const contentW = pageW - margin * 2;
  let y = 0;

  // ─── BACKGROUND ───────────────────────────────────────────────────────────
  drawRect(doc, 0, 0, pageW, pageH, COLORS.darkBg);

  // ─── HEADER BAND ──────────────────────────────────────────────────────────
  drawRect(doc, 0, 0, pageW, 38, COLORS.primary);

  // App name / logo text
  setFont(doc, 18, 'bold', COLORS.white);
  doc.text('FSS JOB CARD', margin, 14);

  setFont(doc, 8, 'normal', [200, 220, 255]);
  doc.text('Workshop & Fleet Management Terminal', margin, 20);

  // Job number badge
  const jobBadgeW = 52;
  drawRect(doc, pageW - margin - jobBadgeW, 6, jobBadgeW, 14, [20, 60, 160], 2);
  setFont(doc, 7, 'normal', [180, 200, 255]);
  doc.text('JOB NUMBER', pageW - margin - jobBadgeW + 3, 12);
  setFont(doc, 11, 'bold', COLORS.white);
  doc.text(data.jobNumber, pageW - margin - jobBadgeW + 3, 18);

  // Date line
  setFont(doc, 7.5, 'normal', [200, 220, 255]);
  doc.text(`Date: ${data.date}   Branch: ${data.branch}`, margin, 30);

  // Status badge
  drawRect(doc, pageW - margin - 30, 25, 30, 8, COLORS.positive, 2);
  setFont(doc, 7, 'bold', COLORS.darkBg);
  doc.text('COMPLETED', pageW - margin - 27, 30.5);

  y = 46;

  // ─── VEHICLE & ASSIGNMENT SECTION ─────────────────────────────────────────
  drawBorderedRect(doc, margin, y, contentW, 32, COLORS.sectionBg, COLORS.borderGray);

  // Section label
  drawRect(doc, margin, y, 28, 6, COLORS.primary, 0);
  setFont(doc, 6.5, 'bold', COLORS.white);
  doc.text('VEHICLE DETAILS', margin + 2, y + 4.5);

  y += 9;
  const colW = contentW / 4;

  const vehicleFields = [
    { label: 'REGISTRATION', value: data.vehicleReg },
    { label: 'VIN', value: data.vin },
    { label: 'MAKE / MODEL', value: `${data.make} ${data.model}` },
    { label: 'YEAR / MILEAGE', value: `${data.year} / ${data.mileage} km` },
  ];

  vehicleFields.forEach((field, i) => {
    const fx = margin + i * colW + 3;
    setFont(doc, 6, 'normal', COLORS.midGray);
    doc.text(field.label, fx, y);
    setFont(doc, 8, 'bold', COLORS.white);
    doc.text(field.value || '—', fx, y + 5);
  });

  y += 14;

  const assignFields = [
    { label: 'TECHNICIAN', value: data.technician },
    { label: 'BRANCH', value: data.branch },
    { label: 'SUBMITTED', value: data.date },
    { label: 'REPORT ID', value: data.jobNumber },
  ];

  assignFields.forEach((field, i) => {
    const fx = margin + i * colW + 3;
    setFont(doc, 6, 'normal', COLORS.midGray);
    doc.text(field.label, fx, y);
    setFont(doc, 8, 'bold', COLORS.white);
    doc.text(field.value || '—', fx, y + 5);
  });

  y += 16;

  // ─── HELPER: DESCRIPTION SECTION ──────────────────────────────────────────
  function drawDescriptionSection(
    title: string,
    stageLabel: string,
    content: string,
    accentColor: [number, number, number],
    startY: number
  ): number {
    const textContent = content || '— No information provided —';
    const lines = doc.splitTextToSize(textContent, contentW - 16);
    const textHeight = lines.length * 4.5;
    const boxH = textHeight + 20;

    drawBorderedRect(doc, margin, startY, contentW, boxH, COLORS.sectionBg, COLORS.borderGray);

    // Accent left bar
    drawRect(doc, margin, startY, 3, boxH, accentColor);

    // Stage badge
    drawRect(doc, margin + 5, startY + 4, 22, 6, accentColor, 1);
    setFont(doc, 6, 'bold', COLORS.darkBg);
    doc.text(stageLabel, margin + 6.5, startY + 8);

    // Title
    setFont(doc, 8.5, 'bold', COLORS.white);
    doc.text(title, margin + 30, startY + 8.5);

    // Content
    setFont(doc, 8, 'normal', COLORS.lightGray);
    doc.text(lines, margin + 8, startY + 17);

    return startY + boxH + 4;
  }

  // ─── STAGE 1: FAULT REPORTED ───────────────────────────────────────────────
  y = drawDescriptionSection(
    'Fault Reported by Driver',
    'STAGE 1',
    data.faultReported,
    COLORS.danger,
    y
  );

  // ─── STAGE 2: FAULTS FOUND ────────────────────────────────────────────────
  y = drawDescriptionSection(
    'Faults Found — Technician Diagnosis',
    'STAGE 2',
    data.faultsFound,
    COLORS.warning,
    y
  );

  // ─── STAGE 3: REPAIRS DONE ────────────────────────────────────────────────
  y = drawDescriptionSection(
    'Repairs Done',
    'STAGE 3',
    data.repairsDone,
    COLORS.positive,
    y
  );

  // ─── PARTS USED ───────────────────────────────────────────────────────────
  if (data.parts && data.parts.length > 0) {
    const partsRowH = 7;
    const partsBoxH = 14 + data.parts.length * partsRowH + 4;

    drawBorderedRect(doc, margin, y, contentW, partsBoxH, COLORS.sectionBg, COLORS.borderGray);
    drawRect(doc, margin, y, 3, partsBoxH, COLORS.primary);

    // Header
    drawRect(doc, margin + 3, y, contentW - 3, 10, [25, 45, 80]);
    setFont(doc, 7, 'bold', COLORS.midGray);
    doc.text('PART NUMBER', margin + 6, y + 7);
    doc.text('PART NAME', margin + 42, y + 7);
    doc.text('QTY USED', pageW - margin - 20, y + 7);

    let ry = y + 14;
    data.parts.forEach((part, idx) => {
      if (idx % 2 === 0) {
        drawRect(doc, margin + 3, ry - 4, contentW - 3, partsRowH, [18, 28, 48]);
      }
      setFont(doc, 7.5, 'bold', [150, 200, 255]);
      doc.text(part.partNumber, margin + 6, ry);
      setFont(doc, 7.5, 'normal', COLORS.white);
      doc.text(part.partName, margin + 42, ry);
      setFont(doc, 7.5, 'bold', COLORS.white);
      doc.text(String(part.qty), pageW - margin - 16, ry);
      ry += partsRowH;
    });

    y += partsBoxH + 4;
  } else {
    // No parts box
    drawBorderedRect(doc, margin, y, contentW, 14, COLORS.sectionBg, COLORS.borderGray);
    drawRect(doc, margin, y, 3, 14, COLORS.primary);
    setFont(doc, 7.5, 'normal', COLORS.midGray);
    doc.text('PARTS USED: No parts recorded for this job card.', margin + 8, y + 9);
    y += 18;
  }

  // ─── SIGNATURE & SIGN-OFF ─────────────────────────────────────────────────
  const sigBoxH = 48;
  // Check if we need a new page
  if (y + sigBoxH + 20 > pageH - 14) {
    doc.addPage();
    drawRect(doc, 0, 0, pageW, pageH, COLORS.darkBg);
    y = 14;
  }

  drawBorderedRect(doc, margin, y, contentW, sigBoxH, COLORS.sectionBg, COLORS.borderGray);
  drawRect(doc, margin, y, 3, sigBoxH, COLORS.positive);

  // Label
  drawRect(doc, margin + 3, y, contentW - 3, 10, [20, 40, 30]);
  setFont(doc, 7, 'bold', COLORS.midGray);
  doc.text('BRANCH REPRESENTATIVE SIGN-OFF', margin + 6, y + 7);

  // Rep name
  setFont(doc, 7, 'normal', COLORS.midGray);
  doc.text('Authorised By:', margin + 6, y + 16);
  setFont(doc, 9, 'bold', COLORS.white);
  doc.text(data.branchRepName || '—', margin + 6, y + 22);

  // Signature image
  if (data.signatureData && data.signatureData.startsWith('data:image')) {
    try {
      const sigX = pageW / 2 + 4;
      const sigW = contentW / 2 - 4;
      const sigH = 32;

      drawBorderedRect(doc, sigX, y + 10, sigW, sigH, [10, 18, 35], COLORS.borderGray, 2);
      doc.addImage(data.signatureData, 'PNG', sigX + 2, y + 12, sigW - 4, sigH - 4);

      setFont(doc, 6, 'normal', COLORS.midGray);
      doc.text('Digital Signature', sigX + 2, y + sigH + 14);
    } catch {
      setFont(doc, 7, 'normal', COLORS.midGray);
      doc.text('[Signature image could not be rendered]', pageW / 2 + 6, y + 28);
    }
  } else {
    setFont(doc, 7, 'normal', COLORS.midGray);
    doc.text('[No signature captured]', pageW / 2 + 6, y + 28);
  }

  y += sigBoxH + 6;

  // ─── FOOTER ───────────────────────────────────────────────────────────────
  const footerY = pageH - 12;
  drawRect(doc, 0, footerY - 4, pageW, 16, [10, 15, 30]);
  doc.setDrawColor(...COLORS.borderGray);
  doc.setLineWidth(0.3);
  doc.line(margin, footerY - 4, pageW - margin, footerY - 4);

  setFont(doc, 6.5, 'normal', COLORS.midGray);
  doc.text(`FSS Job Card System  •  ${data.branch}  •  ${data.vehicleReg}  •  ${data.jobNumber}`, margin, footerY + 2);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-ZA')}`, pageW - margin, footerY + 2, { align: 'right' });

  return doc;
}
